import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import Helmet from 'react-helmet';
import constants from '../config';
import { resolveCardImage } from '../utils/resolveImage';
import { arrangeCardsForGrid } from '../utils/arrangeGrid';
import AuthService from './AuthService';
import AuthModalContext from '../contexts/AuthModalContext';
import LazyCardImage from './LazyCardImage';
import { GameContext } from '../contexts/GameContext';
import './Homepage.css';

const Auth = new AuthService();

// Set carousel configuration — hero slide first, then one slide per booster set
var SET_CAROUSEL_CONFIG = [
  {
    name: 'Primal TCG Singles',
    tagline: 'Buy & Sell',
    isHero: true,
    gallery: ['/cards/set-lr-ap-1.jpg', '/cards/set-lr-ws-1.jpg', '/cards/set-lr-ng-1.jpg', '/cards/set-lr-bb-1.jpg'],
  },
  {
    name: 'Awakened Primordials',
    tagline: 'Where Legends Awaken',
    localImage: null,
    // Multiple LR artworks — crossfade gallery effect
    gallery: [
      '/cards/set-lr-ap-3.jpg',  // Skull demon (dark — under gradient)
      '/cards/set-lr-ap-1.jpg',  // Dragon (red/lightning — dramatic center)
      '/cards/set-lr-ap-4.jpg',  // Plague doctor (moody purple — transition)
      '/cards/set-lr-ap-2.jpg',  // Pirate girl (brightest — right edge)
    ],
    browseSlug: 'Awakened Primordials',
  },
  {
    name: 'Battlers of Bethesda',
    tagline: 'Enter the Arena',
    localImage: null,
    gallery: [
      '/cards/set-lr-bb-3.jpg',  // Purple fighter (dark — under gradient)
      '/cards/set-lr-bb-1.jpg',  // Fire guy (warm flames emerge from dark)
      '/cards/set-lr-bb-4.jpg',  // Angel warrior (golden wings — most visible)
      '/cards/set-lr-bb-2.jpg',  // Mechanic (cool blue bleeds off edge)
    ],
    browseSlug: 'Battlers of Bethesda',
  },
  {
    name: 'Warden of the Seven Deadly Sins',
    tagline: 'Temptation Awaits',
    localImage: null,
    gallery: [
      '/cards/set-lr-ws-1.jpg',  // Hearts girl (dark — under gradient)
      '/cards/set-lr-ws-3.jpg',  // Clubs tree guy (earthy green — transition)
      '/cards/set-lr-ws-2.jpg',  // Sword knight (bright blue/white — center focal)
      '/cards/set-lr-ws-4.jpg',  // Spades sailor (purple — bleeds off edge)
    ],
    browseSlug: 'Warden of the Seven Deadly Sins',
  },
  {
    name: 'New Genesis',
    tagline: 'A New Era Dawns',
    localImage: null,
    gallery: [
      '/cards/set-lr-ng-3.jpg',  // Purple robe (dark — under gradient)
      '/cards/set-lr-ng-1.jpg',  // Red captain (warm red/gold — emergence)
      '/cards/set-lr-ng-2.jpg',  // Shark warrior (colorful teal — center pop)
      '/cards/set-lr-ng-4.jpg',  // Pumpkin girl (bright orange — edge bleed)
    ],
    browseSlug: 'New Genesis',
  },
];

class Homepage extends Component {
  static contextType = AuthModalContext;

  constructor(props) {
    super(props);
    // Load cached data from sessionStorage for instant repeat visits
    var cachedCards = null;
    var cachedListings = null;
    var cachedSpotlight = null;
    var cachedStats = null;
    var cachedCarousel = null;
    try {
      var c = sessionStorage.getItem('hp_featured');
      if (c) cachedCards = JSON.parse(c);
      var l = sessionStorage.getItem('hp_listings');
      if (l) cachedListings = JSON.parse(l);
      var s = sessionStorage.getItem('hp_spotlight');
      if (s) cachedSpotlight = JSON.parse(s);
      var st = sessionStorage.getItem('hp_stats');
      if (st) cachedStats = JSON.parse(st);
      var cr = sessionStorage.getItem('hp_carousel');
      if (cr) cachedCarousel = JSON.parse(cr);
    } catch (e) { /* ignore */ }
    // Preload first carousel slide image(s)
    var firstSet = SET_CAROUSEL_CONFIG[0];
    if (firstSet.gallery && firstSet.gallery.length > 0) {
      // Preload first gallery image for instant render, lazy-load the rest
      var preImg = new Image();
      preImg.src = firstSet.gallery[0];
    } else if (firstSet.localImage) {
      var preImg = new Image();
      preImg.src = firstSet.localImage;
    }
    this.state = {
      posts: cachedListings || [],
      loading: !cachedListings,
      featuredCards: cachedCards || [],
      featuredLoading: !cachedCards,
      totalCards: cachedStats ? cachedStats.totalCards : 0,
      setCount: cachedStats ? cachedStats.setCount : 0,
      showBackToTop: false,
      spotlight: cachedSpotlight || null,
      carouselData: cachedCarousel || null,
      carouselLoading: !cachedCarousel,
      carouselIndex: 0,
      carouselPaused: false,
      _trackPos: 1,
      _noTransition: false,
      _isWrap: false,
    };
    this._carouselTimer = null;
    this._carouselResumeTimer = null;
    this._carouselTouchStartX = 0;
  }

  componentDidMount() {
    this.fetchRecentListings();
    this.fetchFeaturedCards();
    this.fetchCatalogStats();
    this.fetchSpotlight();
    this.fetchSetCarousel();
    this.startCarouselTimer();

    // Scroll-to-top button visibility
    this._scrollHandler = function() {
      var shouldShow = window.scrollY > 800;
      if (shouldShow !== this.state.showBackToTop) {
        this.setState({ showBackToTop: shouldShow });
      }
    }.bind(this);
    window.addEventListener('scroll', this._scrollHandler);

    // Re-render on theme change so spotlight + inline styles update
    this._handleThemeChanged = function() { this.forceUpdate(); }.bind(this);
    window.addEventListener('themeChanged', this._handleThemeChanged);

  }

  componentWillUnmount() {
    if (this._scrollHandler) window.removeEventListener('scroll', this._scrollHandler);
    if (this._handleThemeChanged) window.removeEventListener('themeChanged', this._handleThemeChanged);
    if (this._carouselTimer) clearInterval(this._carouselTimer);
    if (this._carouselResumeTimer) clearTimeout(this._carouselResumeTimer);
  }

  fetchFeaturedCards() {
    var wasCached = !this.state.featuredLoading;
    var gameSlug = this._gameCtx && this._gameCtx.currentGame && this._gameCtx.currentGame.slug;
    var gameParam = gameSlug ? '&game=' + encodeURIComponent(gameSlug) : '';
    fetch(constants.DOMAIN + '/api/v1/guest/cards/featured?limit=12' + gameParam, { mode: 'cors', method: 'GET' })
      .then(function(res) { return res.json(); })
      .then(function(data) {
        var cards = data.cards || [];
        try { sessionStorage.setItem('hp_featured', JSON.stringify(cards)); } catch (e) { /* ignore */ }
        // If cached data is already showing, just update cache silently — don't re-render
        if (wasCached) return;
        this.setState({ featuredCards: cards, featuredLoading: false });
      }.bind(this))
      .catch(function(err) {
        console.log('Error fetching featured cards:', err);
        this.setState({ featuredLoading: false });
      }.bind(this));
  }

  fetchCatalogStats() {
    fetch(constants.DOMAIN + '/api/v1/guest/sets', { mode: 'cors', method: 'GET' })
      .then(function(res) { return res.json(); })
      .then(function(data) {
        var sets = data.sets || [];
        var total = 0;
        sets.forEach(function(s) { total += s.cardCount || 0; });
        this.setState({ totalCards: total, setCount: sets.length });
        try { sessionStorage.setItem('hp_stats', JSON.stringify({ totalCards: total, setCount: sets.length })); } catch (e) { /* ignore */ }
      }.bind(this))
      .catch(function(err) {
        console.log('Error fetching catalog stats:', err);
      }.bind(this));
  }


  fetchRecentListings() {
    var self = this;
    var wasCached = !this.state.loading;
    var url = constants.DOMAIN + '/api/v1/guest/listings?limit=12&sort=newest';

    fetch(url, { mode: 'cors', method: 'GET' })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        var groups = data.groups || [];
        try { sessionStorage.setItem('hp_listings', JSON.stringify(groups)); } catch (e) { /* ignore */ }
        if (wasCached) return; // Cache already showing, skip re-render
        self.setState({ posts: groups, loading: false });
      })
      .catch(function(err) {
        console.log('Error fetching listings:', err);
        self.setState({ loading: false });
      });
  }

  fetchSpotlight() {
    var self = this;
    var hadCachedSpotlight = !!this.state.spotlight;
    fetch(constants.DOMAIN + '/api/v1/spotlight', { mode: 'cors', method: 'GET' })
      .then(function(res) { return res.json(); })
      .then(function(data) {
        if (data && data.enabled) {
          try { sessionStorage.setItem('hp_spotlight', JSON.stringify(data)); } catch (e) { /* ignore */ }
          if (!hadCachedSpotlight) self.setState({ spotlight: data });
        } else {
          try { sessionStorage.removeItem('hp_spotlight'); } catch (e) { /* ignore */ }
          if (hadCachedSpotlight) self.setState({ spotlight: null });
        }
      })
      .catch(function() { /* ignore */ });
  }

  fetchSetCarousel() {
    var wasCached = !!this.state.carouselData;
    fetch(constants.DOMAIN + '/api/v1/guest/set-carousel', { mode: 'cors', method: 'GET' })
      .then(function(res) { return res.json(); })
      .then(function(data) {
        var sets = data.sets || [];
        try { sessionStorage.setItem('hp_carousel', JSON.stringify(sets)); } catch (e) { /* ignore */ }
        if (wasCached) return;
        this.setState({ carouselData: sets, carouselLoading: false });
      }.bind(this))
      .catch(function(err) {
        console.log('Error fetching carousel data:', err);
        this.setState({ carouselLoading: false });
      }.bind(this));
  }

  getSlideCount() {
    var count = SET_CAROUSEL_CONFIG.length;
    if (this.state.spotlight) count += 1; // spotlight slide inserted after hero
    return count;
  }

  startCarouselTimer() {
    if (this._carouselTimer) clearInterval(this._carouselTimer);
    this._carouselTimer = setInterval(function() {
      if (this.state.carouselPaused) return;
      var total = this.getSlideCount();
      var current = this.state.carouselIndex;
      var next = (current + 1) % total;

      if (current === total - 1) {
        // Wrap forward: animate to clone, delay carouselIndex until snap
        this.setState({ _trackPos: total + 1 });
      } else {
        this.setState({ carouselIndex: next, _trackPos: next + 1, _isWrap: false });
      }
    }.bind(this), 6000);
  }

  goToCarouselSlide(index) {
    var total = this.getSlideCount();
    var current = this.state.carouselIndex;

    this.setState({ carouselPaused: true });
    if (this._carouselResumeTimer) clearTimeout(this._carouselResumeTimer);
    this._carouselResumeTimer = setTimeout(function() {
      this.setState({ carouselPaused: false });
    }.bind(this), 3000);

    // Forward wrap: last → first (delay carouselIndex until snap)
    if (current === total - 1 && index === 0) {
      this.setState({ _trackPos: total + 1 });
      return;
    }
    // Backward wrap: first → last (delay carouselIndex until snap)
    if (current === 0 && index === total - 1) {
      this.setState({ _trackPos: 0 });
      return;
    }

    this.setState({ carouselIndex: index, _trackPos: index + 1, _isWrap: false });
  }

  handleCarouselTransitionEnd(e) {
    // Only handle the track's own transform transition
    if (e.target !== e.currentTarget) return;
    if (e.propertyName !== 'transform') return;

    var total = this.getSlideCount();
    var trackPos = this.state._trackPos;

    // At clone-first position: snap back to real first and update carouselIndex
    if (trackPos === total + 1) {
      this.setState({ _noTransition: true, _trackPos: 1, _isWrap: true }, function() {
        void document.body.offsetHeight; // force reflow
        this.setState({ _noTransition: false, carouselIndex: 0 });
      }.bind(this));
    }
    // At clone-last position: snap back to real last and update carouselIndex
    else if (trackPos === 0) {
      this.setState({ _noTransition: true, _trackPos: total, _isWrap: true }, function() {
        void document.body.offsetHeight;
        this.setState({ _noTransition: false, carouselIndex: total - 1 });
      }.bind(this));
    }
  }

  handleCarouselTouchStart(e) {
    this._carouselTouchStartX = e.touches[0].clientX;
  }

  handleCarouselTouchEnd(e) {
    var diff = this._carouselTouchStartX - e.changedTouches[0].clientX;
    var total = this.getSlideCount();
    if (Math.abs(diff) > 50) {
      var current = this.state.carouselIndex;
      var next = diff > 0
        ? (current + 1) % total
        : (current - 1 + total) % total;
      this.goToCarouselSlide(next);
    }
  }

  getCarouselSetImage(config) {
    // Use local image if available, otherwise fall back to setImages from GameContext
    if (config.localImage) return config.localImage;
    var gameCtx = this._gameCtx;
    if (gameCtx && gameCtx.currentGame && gameCtx.currentGame.setImages && gameCtx.currentGame.setImages[config.name]) {
      return gameCtx.currentGame.setImages[config.name];
    }
    return null;
  }

  renderCarouselSlide(setConf, logicalIdx, dataMap, key, isClone) {
    var carouselIndex = this.state.carouselIndex;
    var imageUrl = this.getCarouselSetImage(setConf);
    var hasGallery = setConf.gallery && setConf.gallery.length > 1;
    var data = dataMap[setConf.name] || {};
    var gmv = data.gmv != null ? data.gmv : null;
    var gmvChange = data.gmvChange != null ? data.gmvChange : null;
    var listings = data.activeListings != null ? data.activeListings : null;

    var formatGmv = function(val) {
      if (val == null) return '--';
      if (val >= 1000) return '$' + (val / 1000).toFixed(1) + 'k';
      return '$' + val.toFixed(0);
    };

    var changeColor = gmvChange > 0 ? '#22c55e' : gmvChange < 0 ? '#ef4444' : '#999';
    var changePrefix = gmvChange > 0 ? '+' : '';
    var isActive = logicalIdx === carouselIndex;
    var isWrap = isActive && !isClone && this.state._isWrap;
    var slideClass = 'hp-carousel-slide' + (isActive ? ' active' : '') + (isClone ? ' hp-carousel-clone' : '') + (isWrap ? ' wrap-stagger' : '');

    return (
      <div key={key} className={slideClass}>
        <div className="hp-carousel-art-bg">
          {hasGallery ? (
            <div className="hp-panorama">
              {setConf.gallery.map(function(src, gi) {
                return (
                  <div key={gi} className="hp-panorama-img">
                    <img
                      src={src}
                      alt={setConf.name + ' artwork ' + (gi + 1)}
                      loading={isClone ? 'lazy' : (gi === 0 ? 'eager' : 'lazy')}
                    />
                  </div>
                );
              })}
            </div>
          ) : setConf.gallery && setConf.gallery.length === 1 ? (
            <img src={setConf.gallery[0]} alt={setConf.name} className="hp-single-img" loading="lazy" />
          ) : imageUrl ? (
            <img src={imageUrl} alt={setConf.name} className="hp-single-img" loading="lazy" />
          ) : null}
          <div className="hp-carousel-art-vignette" />
        </div>
        <div className="hp-carousel-art-fade" />

        <div className="hp-carousel-text">
          {!setConf.isHero && (
            <span
              className="tw-text-xs tw-uppercase tw-tracking-widest tw-font-semibold tw-mb-3 tw-inline-block"
              style={{ color: '#c9a84c', letterSpacing: '3px' }}
            >
              {setConf.tagline}
            </span>
          )}
          <h2
            className={'tw-font-bold tw-text-white tw-mb-6 ' + (setConf.isHero ? 'tw-text-3xl md:tw-text-5xl' : 'tw-text-2xl md:tw-text-4xl')}
            style={{ lineHeight: 1.15 }}
          >
            {setConf.isHero ? (
              <React.Fragment>Buy &amp; Sell<br /><span style={{ whiteSpace: 'nowrap' }}>Primal TCG Singles</span></React.Fragment>
            ) : setConf.name}
          </h2>

          {setConf.isHero ? (
            /* Hero slide: subtitle + trust strip */
            <React.Fragment>
              <p className="tw-text-gray-400 tw-text-base md:tw-text-lg tw-mb-4 tw-max-w-md" style={{ lineHeight: 1.5 }}>
                The trusted marketplace for Primal TCG collectors.
              </p>
              <div className="tw-flex tw-items-center tw-gap-5 tw-mb-6" style={{ color: '#9ca3af', fontSize: '11px' }}>
                <span className="tw-flex tw-items-center tw-gap-1.5">
                  <i className="fa fa-shield" style={{ color: '#c9a84c', fontSize: '13px' }} />
                  Escrow Protection
                </span>
                <span className="tw-flex tw-items-center tw-gap-1.5">
                  <i className="fa fa-lock" style={{ color: '#c9a84c', fontSize: '13px' }} />
                  Secure Payments
                </span>
                <span className="tw-flex tw-items-center tw-gap-1.5">
                  <i className="fa fa-truck" style={{ color: '#c9a84c', fontSize: '13px' }} />
                  Tracked Shipping
                </span>
              </div>
            </React.Fragment>
          ) : setConf.isSpotlight ? (
            /* Spotlight slide: spotlight title */
            <p className="tw-text-gray-400 tw-text-sm tw-mb-6">{setConf.spotlightTitle || ''}</p>
          ) : setConf.comingSoon ? (
            <div className="tw-flex tw-gap-3 tw-flex-wrap tw-mb-6">
              <span
                className="tw-inline-block tw-text-xs tw-uppercase tw-tracking-widest tw-font-bold tw-px-4 tw-py-1.5 tw-rounded-full"
                style={{ background: 'rgba(201, 168, 76, 0.15)', color: '#c9a84c', border: '1px solid rgba(201, 168, 76, 0.3)' }}
              >
                Coming Soon
              </span>
            </div>
          ) : gmv >= 500 ? (
            <div className="tw-flex tw-gap-3 tw-flex-wrap tw-mb-6">
              <div className="hp-carousel-stat">
                <span className="tw-text-[10px] tw-uppercase tw-tracking-wider" style={{ color: '#999' }}>
                  Market Volume
                </span>
                <span className="tw-text-lg tw-font-bold tw-text-white tw-mt-0.5">
                  {formatGmv(gmv)}
                </span>
              </div>
              <div className="hp-carousel-stat">
                <span className="tw-text-[10px] tw-uppercase tw-tracking-wider" style={{ color: '#999' }}>
                  30d Change
                </span>
                <span className="tw-text-lg tw-font-bold tw-mt-0.5" style={{ color: changeColor }}>
                  {gmvChange != null ? changePrefix + gmvChange.toFixed(1) + '%' : '--'}
                </span>
              </div>
              <div className="hp-carousel-stat">
                <span className="tw-text-[10px] tw-uppercase tw-tracking-wider" style={{ color: '#999' }}>
                  Active Listings
                </span>
                <span className="tw-text-lg tw-font-bold tw-text-white tw-mt-0.5">
                  {listings != null ? listings : '--'}
                </span>
              </div>
            </div>
          ) : null}

          <div>
            {setConf.isHero ? (
              Auth.loggedIn() ? (
                <Link
                  to="/sell"
                  className="hp-hero-btn-gold tw-inline-block tw-bg-brand-gold tw-text-black tw-font-semibold tw-px-10 tw-py-3 tw-rounded-full tw-no-underline tw-text-sm tw-tracking-wide"
                >
                  Start Selling <i className="fa fa-arrow-right tw-ml-1.5" style={{ fontSize: '11px' }} />
                </Link>
              ) : (
                <button
                  onClick={function() { this.context.openAuthModal('signup'); }.bind(this)}
                  className="hp-hero-btn-gold tw-bg-brand-gold tw-text-black tw-font-semibold tw-px-10 tw-py-3 tw-rounded-full tw-border-0 tw-cursor-pointer tw-text-sm tw-tracking-wide"
                >
                  Start Selling <i className="fa fa-arrow-right tw-ml-1.5" style={{ fontSize: '11px' }} />
                </button>
              )
            ) : setConf.isSpotlight ? (
              setConf.spotlightCtaLink ? (
                setConf.spotlightCtaLink.indexOf('http') === 0 ? (
                  <a
                    href={setConf.spotlightCtaLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tw-inline-block tw-bg-brand-gold tw-text-black tw-font-semibold tw-px-8 tw-py-2.5 tw-rounded-full tw-no-underline tw-text-sm tw-tracking-wide"
                    style={{ boxShadow: '0 4px 20px rgba(201, 168, 76, 0.3)', transition: 'transform 0.25s ease, box-shadow 0.25s ease' }}
                    onMouseEnter={function(e) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 28px rgba(201,168,76,0.45)'; }}
                    onMouseLeave={function(e) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(201,168,76,0.3)'; }}
                  >
                    {setConf.spotlightCtaText || 'Learn More'} <i className="fa fa-arrow-right tw-ml-1.5" style={{ fontSize: '11px' }} />
                  </a>
                ) : (
                  <Link
                    to={setConf.spotlightCtaLink}
                    className="tw-inline-block tw-bg-brand-gold tw-text-black tw-font-semibold tw-px-8 tw-py-2.5 tw-rounded-full tw-no-underline tw-text-sm tw-tracking-wide"
                    style={{ boxShadow: '0 4px 20px rgba(201, 168, 76, 0.3)', transition: 'transform 0.25s ease, box-shadow 0.25s ease' }}
                    onMouseEnter={function(e) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 28px rgba(201,168,76,0.45)'; }}
                    onMouseLeave={function(e) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(201,168,76,0.3)'; }}
                  >
                    {setConf.spotlightCtaText || 'Learn More'} <i className="fa fa-arrow-right tw-ml-1.5" style={{ fontSize: '11px' }} />
                  </Link>
                )
              ) : null
            ) : setConf.comingSoon ? (
              <span
                className="tw-inline-block tw-font-semibold tw-px-8 tw-py-2.5 tw-rounded-full tw-text-sm tw-tracking-wide"
                style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}
              >
                Stay Tuned
              </span>
            ) : (
              <Link
                to={'/browse/primal-tcg/' + encodeURIComponent(setConf.browseSlug)}
                className="tw-inline-block tw-bg-brand-gold tw-text-black tw-font-semibold tw-px-8 tw-py-2.5 tw-rounded-full tw-no-underline tw-text-sm tw-tracking-wide"
                style={{ boxShadow: '0 4px 20px rgba(201, 168, 76, 0.3)', transition: 'transform 0.25s ease, box-shadow 0.25s ease' }}
                onMouseEnter={function(e) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 28px rgba(201,168,76,0.45)'; }}
                onMouseLeave={function(e) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(201,168,76,0.3)'; }}
              >
                Shop Set <i className="fa fa-arrow-right tw-ml-1.5" style={{ fontSize: '11px' }} />
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  renderSetCarousel() {
    var carouselData = this.state.carouselData;
    var carouselIndex = this.state.carouselIndex;
    var trackPos = this.state._trackPos;
    var noTransition = this.state._noTransition;

    // Build dynamic slides: hero + optional spotlight + set slides
    var slides = [SET_CAROUSEL_CONFIG[0]]; // hero
    if (this.state.spotlight) {
      var sp = this.state.spotlight;
      slides.push({
        name: sp.title || 'Spotlight',
        tagline: 'Featured',
        isSpotlight: true,
        spotlightTitle: sp.title || '',
        spotlightCtaText: sp.ctaText || '',
        spotlightCtaLink: sp.ctaLink || '',
        gallery: sp.image ? [constants.DOMAIN + '/' + sp.image] : [],
      });
    }
    slides = slides.concat(SET_CAROUSEL_CONFIG.slice(1));
    var total = slides.length;

    // Build data lookup
    var dataMap = {};
    if (carouselData && Array.isArray(carouselData)) {
      carouselData.forEach(function(s) { dataMap[s.name] = s; });
    }

    var trackStyle = { transform: 'translateX(-' + (trackPos * 100) + '%)' };
    if (noTransition) trackStyle.transition = 'none';

    return (
      <section className="hp-carousel">
        <div className="hp-carousel-container">
        {/* Slide track — clones at each end for seamless wrap */}
        <div
          className="hp-carousel-track"
          style={trackStyle}
          onTransitionEnd={function(e) { this.handleCarouselTransitionEnd(e); }.bind(this)}
          onTouchStart={function(e) { this.handleCarouselTouchStart(e); }.bind(this)}
          onTouchEnd={function(e) { this.handleCarouselTouchEnd(e); }.bind(this)}
          onMouseEnter={function() { this.setState({ carouselPaused: true }); }.bind(this)}
          onMouseLeave={function() { this.setState({ carouselPaused: false }); }.bind(this)}
        >
          {/* Clone of last slide (for backward wrap) */}
          {this.renderCarouselSlide(slides[total - 1], total - 1, dataMap, 'clone-last', true)}

          {/* Real slides */}
          {slides.map(function(setConf, idx) {
            return this.renderCarouselSlide(setConf, idx, dataMap, 'slide-' + idx, false);
          }.bind(this))}

          {/* Clone of first slide (for forward wrap) */}
          {this.renderCarouselSlide(slides[0], 0, dataMap, 'clone-first', true)}
        </div>

        {/* Arrow navigation (desktop) */}
        {total > 1 && (
          <button
            className="hp-carousel-arrow hp-carousel-arrow-left"
            onClick={function() { this.goToCarouselSlide((carouselIndex - 1 + total) % total); }.bind(this)}
            aria-label="Previous set"
          >
            <i className="fa fa-chevron-left" />
          </button>
        )}
        {total > 1 && (
          <button
            className="hp-carousel-arrow hp-carousel-arrow-right"
            onClick={function() { this.goToCarouselSlide((carouselIndex + 1) % total); }.bind(this)}
            aria-label="Next set"
          >
            <i className="fa fa-chevron-right" />
          </button>
        )}

        {/* Dot indicators */}
        {total > 1 && (
          <div className="hp-carousel-dots">
            {slides.map(function(_, i) {
              return (
                <button
                  key={i}
                  className={'hp-carousel-dot' + (i === carouselIndex ? ' active' : '')}
                  onClick={function() { this.goToCarouselSlide(i); }.bind(this)}
                  aria-label={'Go to slide ' + (i + 1)}
                />
              );
            }.bind(this))}
          </div>
        )}
        </div>
      </section>
    );
  }

  renderRecentListings() {
    var posts = this.state.posts;
    var loading = this.state.loading;

    return (
      <section className="tw-max-w-6xl tw-mx-auto tw-px-4 tw-py-16">
        <div className="tw-mb-8">
          <div className="tw-flex tw-items-center tw-justify-between">
            <h2 className="tw-text-2xl tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-1">
              Recent Listings
            </h2>
            <Link
              to="/listings"
              className="tw-text-xs tw-uppercase tw-tracking-wider tw-font-semibold tw-text-brand-gold hover:tw-text-brand-gold-dark tw-no-underline tw-transition-colors"
            >
              View All <i className="fa fa-arrow-right tw-ml-1" />
            </Link>
          </div>
          <p className="tw-text-sm tw-text-[#888] dark:tw-text-gray-500 tw-mb-0">
            New listings from the community
          </p>
        </div>

        {loading ? (
          <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 md:tw-grid-cols-4 lg:tw-grid-cols-6 tw-gap-3">
            {[1, 2, 3, 4, 5, 6].map(function(i) {
              return (
                <div
                  key={i}
                  className="tw-bg-[#f5f5f5] dark:tw-bg-[#1a1a1a] tw-rounded-xl tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-overflow-hidden"
                >
                  <div className="tw-aspect-[3/4] hp-skeleton" />
                  <div className="tw-p-2">
                    <div className="tw-h-3 tw-w-3/4 hp-skeleton tw-rounded tw-mb-1.5" />
                    <div className="tw-h-2.5 tw-w-1/2 hp-skeleton tw-rounded" />
                  </div>
                </div>
              );
            })}
          </div>
        ) : posts.length > 0 ? (
          <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 md:tw-grid-cols-4 lg:tw-grid-cols-6 tw-gap-3 tw-items-stretch">
            {arrangeCardsForGrid(posts.map(function(g) { return Object.assign({}, g, { horizontal: g.horizontal === true }); }), 4).map(function(group) {
              var imageUrl = resolveCardImage(group.cardImage);
              var isHoriz = group.horizontal === true;
              var displayName = (group.cardName || '').replace(/\s*\(Foil\)/i, '');

              return (
                <Link
                  key={(group.cardId || '') + (group.foil ? '_foil' : '')}
                  to={'/card/' + group.cardId}
                  className={'tw-bg-[#f5f5f5] dark:tw-bg-[#1a1a1a] tw-rounded-xl tw-border tw-border-solid tw-border-[#e0e0e0] dark:tw-border-[#333] tw-overflow-hidden tw-transition-all tw-duration-200 listing-card-hover tw-flex tw-flex-col' + (isHoriz ? ' horiz-card' : '')}
                  style={{ display: 'flex', textDecoration: 'none' }}
                >
                  <div className={'tw-bg-[#e0e0e0] dark:tw-bg-[#252525] tw-overflow-hidden tw-flex tw-items-center tw-justify-center tw-relative' + (isHoriz ? ' horiz-card-img' : ' tw-aspect-[3/4] tw-py-2')}>
                    <LazyCardImage src={imageUrl} alt={displayName} rotate={isHoriz ? (group.rotation || -90) : 0} />
                    {group.dealPercent >= 15 ? (
                      <div
                        className="tw-absolute tw-top-1.5 tw-right-1.5 tw-text-white tw-font-bold tw-px-1.5 tw-py-0.5 tw-rounded-full"
                        style={{ fontSize: '9px', backgroundColor: '#059669', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }}
                      >
                        {group.dealPercent}% Below Market
                      </div>
                    ) : group.foil ? (
                      <div
                        className="tw-absolute tw-top-1.5 tw-left-1.5 tw-font-bold tw-px-1.5 tw-py-0.5 tw-rounded-full tw-text-black"
                        style={{ fontSize: '9px', background: 'linear-gradient(135deg, #ffd700, #ffaa00, #ffd700)' }}
                      >
                        Foil
                      </div>
                    ) : null}
                    <div className="tw-absolute tw-bottom-1.5 tw-left-1.5 tw-bg-black/75 tw-rounded-full tw-overflow-hidden">
                      <span className="tw-text-brand-gold tw-text-xs tw-font-bold tw-px-2 tw-py-0.5">
                        ${group.lowestPrice ? group.lowestPrice.toFixed(2) : '0.00'}
                      </span>
                    </div>
                  </div>
                  <div className="tw-p-2 tw-flex-1">
                    <p className="tw-text-xs tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-0.5 tw-truncate">
                      {displayName}
                    </p>
                    <p className="tw-text-[10px] tw-text-[#666] dark:tw-text-[#999] tw-mb-0.5 tw-truncate">
                      {group.listingCount} {group.listingCount === 1 ? 'listing' : 'listings'}
                    </p>
                    {group.marketPrice ? (
                      <p className="tw-text-[10px] tw-text-[#666] dark:tw-text-[#999] tw-mb-0 tw-truncate">
                        Mkt: ${group.marketPrice.toFixed(2)}
                        {group.dealPercent > 0 ? (
                          <span className="tw-text-green-500 tw-font-semibold tw-ml-1">
                            {group.dealPercent}%
                          </span>
                        ) : null}
                      </p>
                    ) : null}
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="tw-text-center tw-py-16 tw-px-4 tw-bg-[#f5f5f5] dark:tw-bg-[#1a1a1a] tw-rounded-2xl tw-border tw-border-[#e0e0e0] dark:tw-border-[#333]">
            <div className="tw-text-4xl tw-mb-4" style={{ filter: 'grayscale(0)' }}>
              <svg className="tw-w-12 tw-h-12 tw-mx-auto tw-text-brand-gold tw-opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="tw-text-lg tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-2">
              Be the First to List a Card
            </h3>
            <p className="tw-text-sm tw-text-[#999] dark:tw-text-gray-500 tw-mb-6 tw-max-w-md tw-mx-auto">
              The marketplace is fresh and ready. List your cards now and be among the first sellers on Primal Marketplace.
            </p>
            {Auth.loggedIn() ? (
              <Link
                to="/sell"
                className="tw-inline-block tw-bg-brand-gold hover:tw-bg-brand-gold-dark tw-text-black tw-font-semibold tw-px-10 tw-py-3.5 tw-rounded-full tw-no-underline tw-transition-colors tw-text-base tw-tracking-wide"
                style={{ boxShadow: '0 4px 24px rgba(201, 168, 76, 0.35)' }}
              >
                Start Selling
              </Link>
            ) : (
              <button
                onClick={() => this.context.openAuthModal('signup')}
                className="tw-bg-brand-gold hover:tw-bg-brand-gold-dark tw-text-black tw-font-semibold tw-px-10 tw-py-3.5 tw-rounded-full tw-transition-colors tw-border-0 tw-cursor-pointer tw-text-base tw-tracking-wide"
                style={{ boxShadow: '0 4px 24px rgba(201, 168, 76, 0.35)' }}
              >
                Start Selling
              </button>
            )}
          </div>
        )}
      </section>
    );
  }

  renderHowItWorks() {
    return (
      <section className="tw-py-16 tw-px-4">
        <div
          className="tw-max-w-6xl tw-mx-auto tw-rounded-2xl tw-overflow-hidden tw-relative"
          style={{ minHeight: '320px', color: '#fff', boxShadow: '0 0 60px rgba(201,168,76,0.08), 0 8px 32px rgba(0,0,0,0.3)', border: '1px solid rgba(201,168,76,0.12)' }}
        >
          {/* Card art background — visible with blur for depth */}
          <img src="/cards/how-it-works-banner.jpg" alt="" className="tw-absolute tw-inset-0 tw-w-full tw-h-full" style={{ objectFit: 'cover', objectPosition: 'center 50%', opacity: 0.45, filter: 'saturate(1.2)' }} />
          {/* Radial vignette — dark center for text, art peeks through at edges */}
          <div className="tw-absolute tw-inset-0" style={{ background: 'radial-gradient(ellipse at center, rgba(10,10,30,0.8) 0%, rgba(10,10,30,0.5) 60%, rgba(10,10,30,0.3) 100%)' }} />
          {/* Bottom fade to solid */}
          <div className="tw-absolute tw-inset-0" style={{ background: 'linear-gradient(180deg, transparent 0%, transparent 50%, rgba(10,10,30,0.7) 100%)' }} />
          {/* Subtle gold top accent line */}
          <div className="tw-absolute tw-top-0 tw-left-0 tw-right-0" style={{ height: '2px', background: 'linear-gradient(90deg, transparent 10%, rgba(201,168,76,0.4) 50%, transparent 90%)' }} />
          <div className="tw-relative tw-z-10 tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16 tw-px-6 tw-text-center">
            <h2 className="tw-text-2xl md:tw-text-3xl tw-font-bold tw-text-white tw-mb-3" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.5)' }}>
              How Primal Market Works
            </h2>
            <p style={{ color: '#c8c8d0', textShadow: '0 1px 8px rgba(0,0,0,0.4)' }} className="tw-max-w-lg tw-mb-6">
              The marketplace built for Primal TCG. Buy, sell, and share cards with low fees and built-in buyer protection.
            </p>
            <Link to="/more" className="tw-no-underline" style={{
              display: 'inline-block',
              backgroundColor: '#c9a84c',
              color: '#000',
              fontWeight: '600',
              fontSize: '14px',
              padding: '10px 28px',
              borderRadius: '8px',
              textDecoration: 'none',
              transition: 'all 0.25s ease',
              boxShadow: '0 4px 16px rgba(201,168,76,0.25)'
            }}
              onMouseEnter={function(e) { e.currentTarget.style.backgroundColor = '#b8953f'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(201,168,76,0.4)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={function(e) { e.currentTarget.style.backgroundColor = '#c9a84c'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(201,168,76,0.25)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              Learn More
            </Link>
          </div>
        </div>
      </section>
    );
  }

  renderCommunityBanner() {
    return (
      <section className="tw-py-16 tw-px-4">
        <div
          className="tw-max-w-6xl tw-mx-auto tw-rounded-2xl tw-overflow-hidden tw-relative"
          style={{ minHeight: '320px', color: '#fff', boxShadow: '0 0 60px rgba(201,168,76,0.08), 0 8px 32px rgba(0,0,0,0.3)', border: '1px solid rgba(201,168,76,0.12)' }}
        >
          {/* Card art background — visible with blur for depth */}
          <img src="/cards/community-banner.jpg" alt="" className="tw-absolute tw-inset-0 tw-w-full tw-h-full" style={{ objectFit: 'cover', objectPosition: 'center 40%', opacity: 0.45, filter: 'saturate(1.2)' }} />
          {/* Radial vignette — dark center for text, art peeks through at edges */}
          <div className="tw-absolute tw-inset-0" style={{ background: 'radial-gradient(ellipse at center, rgba(10,10,30,0.8) 0%, rgba(10,10,30,0.5) 60%, rgba(10,10,30,0.3) 100%)' }} />
          {/* Bottom fade to solid */}
          <div className="tw-absolute tw-inset-0" style={{ background: 'linear-gradient(180deg, transparent 0%, transparent 50%, rgba(10,10,30,0.7) 100%)' }} />
          {/* Subtle gold top accent line */}
          <div className="tw-absolute tw-top-0 tw-left-0 tw-right-0" style={{ height: '2px', background: 'linear-gradient(90deg, transparent 10%, rgba(201,168,76,0.4) 50%, transparent 90%)' }} />
          <div className="tw-relative tw-z-10 tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16 tw-px-6 tw-text-center">
            <h2 className="tw-text-2xl md:tw-text-3xl tw-font-bold tw-text-white tw-mb-3" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.5)' }}>
              Join the Primal Community
            </h2>
            <p style={{ color: '#c8c8d0', textShadow: '0 1px 8px rgba(0,0,0,0.4)' }} className="tw-max-w-lg tw-mb-6">
              Thousands of collectors buying and selling every day.
            </p>
            <a
              href="https://discord.com/invite/pxTRcC32nZ"
              target="_blank"
              rel="noopener noreferrer"
              className="tw-no-underline"
              style={{
                display: 'inline-block',
                backgroundColor: '#c9a84c',
                color: '#000',
                fontWeight: '600',
                fontSize: '14px',
                padding: '10px 28px',
                borderRadius: '8px',
                textDecoration: 'none',
                transition: 'all 0.25s ease',
                boxShadow: '0 4px 16px rgba(201,168,76,0.25)'
              }}
              onMouseEnter={function(e) { e.currentTarget.style.backgroundColor = '#b8953f'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(201,168,76,0.4)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={function(e) { e.currentTarget.style.backgroundColor = '#c9a84c'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(201,168,76,0.25)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              Join Discord
            </a>
          </div>
        </div>
      </section>
    );
  }

  render() {
    return (
      <GameContext.Consumer>
      {(gameCtx) => {
        this._gameCtx = gameCtx;
        return (
      <div>
        <Helmet>
          <title>Primal Marketplace — Buy & Sell Primal TCG Cards</title>
          <meta name="description" content="The official marketplace for Primal TCG. Buy and sell cards with collectors nationwide." />
          <meta property="og:title" content="Primal Marketplace — Buy & Sell Primal TCG Cards" />
          <meta property="og:description" content="The official marketplace for Primal TCG. Buy and sell cards with collectors nationwide." />
          <meta property="og:type" content="website" />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:title" content="Primal Marketplace — Buy & Sell Primal TCG Cards" />
          <meta name="twitter:description" content="The official marketplace for Primal TCG. Buy and sell cards with collectors nationwide." />
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Primal Marketplace",
            "url": "https://www.primaltcg.com",
            "description": "The official marketplace for Primal TCG. Buy and sell cards with collectors nationwide."
          })}</script>
        </Helmet>
        <style>{`
          a.listing-card-hover:hover {
            border-color: #c9a84c !important;
            box-shadow: 0 8px 24px rgba(0,0,0,0.08) !important;
            text-decoration: none !important;
            transform: translateY(-3px) !important;
          }
          .tw-dark a.listing-card-hover:hover {
            box-shadow: 0 8px 24px rgba(201,168,76,0.12) !important;
          }
        `}</style>
        {/* Carousel (hero slide first, then sets) */}
        {this.renderSetCarousel()}

        {/* Recent Listings */}
        {this.renderRecentListings()}

        {/* How Primal Market Works */}
        {this.renderHowItWorks()}

        {/* Community Banner */}
        {this.renderCommunityBanner()}

        {/* Scroll-to-top */}
        {this.state.showBackToTop ? (
          <button
            onClick={function() { window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="tw-fixed tw-bottom-6 tw-right-6 tw-w-10 tw-h-10 tw-rounded-full tw-bg-brand-gold tw-text-black tw-border-0 tw-cursor-pointer tw-z-40 tw-flex tw-items-center tw-justify-center mobile-tab-offset-btn"
            style={{ animation: 'hp-fadeUp 0.3s ease forwards', boxShadow: '0 4px 16px rgba(201,168,76,0.3)' }}
            aria-label="Back to top"
          >
            <i className="fa fa-chevron-up" />
          </button>
        ) : null}
      </div>
        );
      }}
      </GameContext.Consumer>
    );
  }
}

export default Homepage;
